import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridToolbarContainer,
  GridRowClassNameParams,
  GridValueGetterParams,
  GridToolbarExport,
} from "@mui/x-data-grid";
import StarIcon from "@mui/icons-material/Star";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Rating,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { Talent, FavoriteTalent } from "../../types/users";
import { useNavigate } from "react-router-dom";
import TalentDrawer from "./TalentDrawer";
import {
  useGetFavoriteTalentsQuery,
  useDeleteFavoriteTalentMutation,
} from "../../services/favoriteTalent.service";
import { CardBase, GlassDataGrid, StyledTextField } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

const TableToolbar = ({ onSearch }: { onSearch: (query?: string) => void; }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const enterToSearchLabel = useUILabel("common.enterToSearch");
  const resetLabel = useUILabel("common.reset");
  const favoriteIndependentProfessionalsLabel = useUILabel("talent.favoriteIndependentProfessionals");
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    onSearch(debounceQuery);
  }, [debounceQuery, onSearch]);

  return (
    <GridToolbarContainer
      sx={{
        padding: { xs: "12px 16px", sm: "16px 20px" },
        marginBottom: 0,
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: { xs: 1.5, sm: 2 },
      }}
    >
      <Box flex={1} sx={{ display: "flex", alignItems: "center", gap: 1, width: { xs: "100%", sm: "auto" } }}>
        <StyledTextField
          margin="dense"
          label={enterToSearchLabel}
          value={query || ""}
          onChange={(e) => setQuery(e.target.value)}
          sx={{
            minWidth: { xs: "100%", sm: 250 },
            flex: { xs: 1, sm: "none" },
          }}
        />
        {!!query && (
          <Button
            variant="text"
            sx={{
              mt: { xs: 0, sm: 1 },
              whiteSpace: "nowrap",
            }}
            onClick={() => setQuery("")}
          >
            {resetLabel}
          </Button>
        )}
      </Box>
      <Stack direction="row" sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{
            allColumns: true,
            fileName: favoriteIndependentProfessionalsLabel,
          }}
        />
      </Stack>
    </GridToolbarContainer>
  );
};

const TalentsFavPage = () => {
  const favoriteIndependentProfessionalsLabel = useUILabel("talent.favoriteIndependentProfessionals");
  const independentProfessionalsLabel = useUILabel("talent.independentProfessionals");
  const ratingLabel = useUILabel("talent.rating");
  const emailLabel = useUILabel("common.email");
  const phonenumberLabel = useUILabel("talent.phonenumber");
  const addressLabel = useUILabel("common.address");
  const cityLabel = useUILabel("common.city");
  const stateLabel = useUILabel("common.state");
  const zipLabel = useUILabel("common.zip");
  const deleteLabel = useUILabel("common.delete");
  const fullNameLabel = useUILabel("common.fullName");
  const talentLabel = useUILabel("talent.talent");
  const facebookLabel = useUILabel("talent.facebook");
  const instagramLabel = useUILabel("talent.instagram");
  const linkedInLabel = useUILabel("talent.linkedIn");
  const successfullyDeletedLabel = useUILabel("talent.successfullyDeleted");
  const deleteFromFavoriteLabel = useUILabel("talent.deleteFromFavorite");
  
  useTitle(favoriteIndependentProfessionalsLabel);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<FavoriteTalent> | null;
  }>({
    open: false,
    row: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Talent | null>(null);

  const { data, isLoading, isFetching } = useGetFavoriteTalentsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete, { isLoading: deleting }] = useDeleteFavoriteTalentMutation();

  const handleDeleteRow = (row: GridRowModel<FavoriteTalent>) => {
    setDeleteModal({ open: true, row });
  };

  const rows: FavoriteTalent[] | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "avatar",
        headerName: "",
        width: isMobile ? 40 : 50,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) return null;
          return (
            <Avatar
              alt={`${params.row.user.first_name || talentLabel}`}
              src={`${params.row.user?.photo?.[0]?.original_url}`}
              sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}
            />
          );
        },
        disableExport: true,
      },
      {
        field: "talent_name",
        headerName: independentProfessionalsLabel,
        flex: 1,
        minWidth: isMobile ? 120 : 150,
        // sortingOrder: ["desc", "asc", null],
        sortable: false,
        filterable: true,
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) return null;
          return (
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setCurrentItem(params.row.user);
                setOpen(true);
              }}
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              {params.row.user.full_name || "N/A"}
            </Link>
          );
        },
        disableExport: true,
      },
      {
        field: "full_name",
        headerName: fullNameLabel,
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.full_name || "";
        },
      },
      {
        field: "rating",
        headerName: ratingLabel,
        headerAlign: "center",
        minWidth: isMobile ? 110 : 150,
        sortable: false,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) return null;
          return (
            <Rating
              value={params.row.user.total_rating || 0}
              readOnly
              precision={0.5}
              size={isMobile ? "small" : "medium"}
            />
          );
        },
        disableExport: true,
      },
      {
        field: "user.email",
        headerName: emailLabel,
        headerAlign: "center",
        minWidth: isMobile ? 150 : 200,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.email || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
              {params.row.user.email || "-"}
            </Typography>
          );
        },
      },
      {
        field: "phone_number",
        headerName: phonenumberLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 150,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.phone_number || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
              {params.row.user.phone_number || "-"}
            </Typography>
          );
        },
      },
      {
        field: "physical_address_1",
        headerName: addressLabel,
        headerAlign: "center",
        minWidth: isMobile ? 150 : 200,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.physical_address_1 || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
              {params.row.user.physical_address_1 || "-"}
            </Typography>
          );
        },
      },
      {
        field: "user.city",
        headerName: cityLabel,
        headerAlign: "center",
        minWidth: isMobile ? 100 : 150,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.city || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
              {params.row.user.city ? params.row.user.city.toUpperCase() : "-"}
            </Typography>
          );
        },
      },
      {
        field: "user.state",
        headerName: stateLabel,
        headerAlign: "center",
        minWidth: isMobile ? 100 : 150,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.state || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
              {params.row.user.state ? params.row.user.state.toUpperCase() : "-"}
            </Typography>
          );
        },
      },
      {
        field: "zip",
        headerName: zipLabel,
        headerAlign: "center",
        minWidth: isMobile ? 80 : 100,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.zip || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
              {params.row.user.zip || "-"}
            </Typography>
          );
        },
      },
      {
        field: "control",
        headerName: deleteLabel,
        headerAlign: "center",
        minWidth: isMobile ? 80 : 130,
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row) return null;
          return (
            <Stack direction={"row"}>
              <IconButton onClick={() => handleDeleteRow(params.row)} size={isMobile ? "small" : "medium"}>
                <DeleteIcon fontSize={isMobile ? "small" : "small"} />
              </IconButton>
            </Stack>
          );
        },
        disableExport: true,
      },
      {
        field: "facebook",
        headerName: facebookLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 150,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.facebook || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          const facebookUrl = params.row.user.facebook;
          if (!facebookUrl) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Link
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" }, wordBreak: "break-all" }}
            >
              {facebookUrl}
            </Link>
          );
        },
      },
      {
        field: "instagram",
        headerName: instagramLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 150,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.instagram || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          const instagramUrl = params.row.user.instagram;
          if (!instagramUrl) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Link
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" }, wordBreak: "break-all" }}
            >
              {instagramUrl}
            </Link>
          );
        },
      },
      {
        field: "linkedin",
        headerName: linkedInLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 150,
        sortable: false,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row || !params.row.user) return "";
          return params.row.user.linkedin || "";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params || !params.row || !params.row.user) {
            return <Typography variant="body2">-</Typography>;
          }
          const linkedinUrl = params.row.user.linkedin;
          if (!linkedinUrl) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Link
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" }, wordBreak: "break-all" }}
            >
              {linkedinUrl}
            </Link>
          );
        },
      },
    ],
    [isMobile, independentProfessionalsLabel, fullNameLabel, ratingLabel, emailLabel, phonenumberLabel, addressLabel, cityLabel, stateLabel, zipLabel, deleteLabel, facebookLabel, instagramLabel, linkedInLabel, talentLabel]
  );

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success(successfullyDeletedLabel);
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const CustomToolbar = React.useMemo(
    () => () => <TableToolbar onSearch={handleSearch} />,
    [handleSearch]
  );

  return (
    <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
      <CardBase
        title={favoriteIndependentProfessionalsLabel}
        useGlassCard={true}
        nested={false}
      >
        <GlassDataGrid
          height={isMobile ? "75vh" : "85vh"}
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page as any}
          onPageChange={(newPage: any) => setPage(newPage)}
          pageSize={pageSize as any}
          rowsPerPageOptions={[1000]}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={React.useCallback((newSortModel: GridSortModel) => {
            setSortModel(newSortModel);
          }, [])}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: LinearProgress,
            NoResultsOverlay: NoResultsOverlay,
            Toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          rowHeight={isMobile ? 60 : 72}
          density="compact"
          sx={{
            "& .MuiDataGrid-toolbarContainer": {
              padding: { xs: "12px 16px", sm: "16px 20px" },
              backgroundColor: "transparent",
              borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(15px) saturate(180%)",
              WebkitBackdropFilter: "blur(15px) saturate(180%)",
              borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#12903C",
              },
            },
          }}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={deleteFromFavoriteLabel.replace("{name}", `${deleteModal.row?.user?.first_name || ""} ${deleteModal.row?.user?.last_name || ""}`)}
      />
      <TalentDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </Box>
  );
};
export default TalentsFavPage;
